import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { ToastModule } from 'primeng/toast';
import { TooltipModule } from 'primeng/tooltip';
import { Permission } from '../../../core/enums/permission.enum';
import { GuardConfiguration, GuardConfigurationStatus } from '../../../core/models/guard-configuration';
import { GuardConfigurationService } from '../../../core/services/guard-configuration.service';
import { TrackedDialogService } from '../../../core/services/tracked-dialog.service';
import { EmPermissionDirective } from '../../../shared/directives/has-permission.directive';
import { GuardConfigurationFormComponent } from './guard-configuration-form/guard-configuration-form.component';

@Component({
  selector: 'em-guard-configuration',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    ButtonModule,
    ConfirmDialogModule,
    TableModule,
    TagModule,
    ToastModule,
    TooltipModule,
    EmPermissionDirective
  ],
  providers: [MessageService, ConfirmationService, TrackedDialogService],
  templateUrl: './guard-configuration.component.html',
  styleUrls: ['./guard-configuration.component.scss']
})
export class GuardConfigurationComponent implements OnInit {
  guardConfigurations: GuardConfiguration[] = [];
  isLoading = false;
  ref: DynamicDialogRef | undefined;
  Permission = Permission;
  GuardConfigurationStatus = GuardConfigurationStatus;

  constructor(
    private guardConfigurationService: GuardConfigurationService,
    private messageService: MessageService,
    private translate: TranslateService,
    private dialogService: DialogService,
    private confirmationService: ConfirmationService
  ) {}

  ngOnInit(): void {
    this.loadGuardConfigurations();
  }

  loadGuardConfigurations(): void {
    this.isLoading = true;
    this.guardConfigurationService.getAll().subscribe({
      next: (guardConfigurations) => {
        this.guardConfigurations = guardConfigurations;
        this.isLoading = false;
      },
      error: () => {
        this.isLoading = false;
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('layout.configurations.guardConfiguration.notifications.errorTitle'),
          detail: this.translate.instant('layout.configurations.guardConfiguration.notifications.loadError')
        });
      }
    });
  }

  onAdd(): void {
    this.openForm();
  }

  onEdit(guardConfiguration: GuardConfiguration): void {
    this.openForm(guardConfiguration);
  }

  onDelete(event: Event, guardConfiguration: GuardConfiguration): void {
    if (!guardConfiguration.id) {
      return;
    }

    this.confirmationService.confirm({
      target: event.target as EventTarget,
      message: this.translate.instant('layout.configurations.guardConfiguration.delete.message'),
      header: this.translate.instant('layout.configurations.guardConfiguration.delete.title'),
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: this.translate.instant('layout.configurations.guardConfiguration.delete.yes'),
      rejectLabel: this.translate.instant('layout.configurations.guardConfiguration.delete.no'),
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => {
        this.guardConfigurationService.delete(guardConfiguration.id as number).subscribe({
          next: () => {
            this.loadGuardConfigurations();
            this.messageService.add({
              severity: 'success',
              summary: this.translate.instant('layout.configurations.guardConfiguration.notifications.successTitle'),
              detail: this.translate.instant('layout.configurations.guardConfiguration.notifications.deleted')
            });
          },
          error: () => {
            this.messageService.add({
              severity: 'error',
              summary: this.translate.instant('layout.configurations.guardConfiguration.notifications.errorTitle'),
              detail: this.translate.instant('layout.configurations.guardConfiguration.notifications.deleteError')
            });
          }
        });
      }
    });
  }

  getStatusLabel(status: string): string {
    return this.translate.instant(`layout.configurations.guardConfiguration.statuses.${status}`);
  }

  getRateLabel(guardConfiguration: GuardConfiguration): string {
    const currency = guardConfiguration.currencyName?.trim();
    const rates = [
      this.formatRateValue(guardConfiguration.laborable),
      this.formatRateValue(guardConfiguration.nonLaborable),
      this.formatRateValue(guardConfiguration.especial)
    ].join('.');
    return currency ? `${currency}_${rates}` : rates;
  }

  getStatusSeverity(status: string): 'success' | 'danger' | 'info' {
    return status === GuardConfigurationStatus.ACTIVE
      ? 'success'
      : status === GuardConfigurationStatus.INACTIVE
        ? 'danger'
        : 'info';
  }

  private openForm(guardConfiguration?: GuardConfiguration): void {
    const isEditMode = !!guardConfiguration;
    this.ref = this.dialogService.open(GuardConfigurationFormComponent, {
      header: this.translate.instant(
        isEditMode
          ? 'layout.configurations.guardConfiguration.edit.title'
          : 'layout.configurations.guardConfiguration.add.title'
      ),
      width: '560px',
      contentStyle: { overflow: 'auto' },
      data: {
        mode: isEditMode ? 'edit' : 'add',
        guardConfiguration
      }
    });

    this.ref.onClose.subscribe((result) => {
      if (!result) {
        return;
      }
      this.loadGuardConfigurations();
      this.messageService.add({
        severity: 'success',
        summary: this.translate.instant('layout.configurations.guardConfiguration.notifications.successTitle'),
        detail: this.translate.instant(
          isEditMode
            ? 'layout.configurations.guardConfiguration.notifications.updated'
            : 'layout.configurations.guardConfiguration.notifications.saved'
        )
      });
    });
  }

  private formatRateValue(value: number | null | undefined): string {
    if (value === null || value === undefined || Number.isNaN(Number(value))) {
      return '';
    }
    return Number(value).toString();
  }
}
